import {
  Button,
  Icon,
  trackEvent,
  Text,
  Spinner,
} from '@requity-homes/component-library';
import { differenceInDays, format, subDays } from 'date-fns';
import currency from 'currency.js';
import { useState, useRef, useEffect } from 'react';
import { GetServerSidePropsContext } from 'next';
import { getCookie } from 'cookies-next';
import {
  capitalizeAllFirstLetters,
  convertStringToFloat,
  cookiesFavoriteListings,
  cookiesIdToken,
  generateListingUrl,
  servedCities,
  RepliersStateToAbbreviatedProvinceMap,
} from '@requity-homes/utils';
import { calculateTotalMonthlyPayment } from '@requity-homes/underwriting';
import {
  Header,
  Footer,
  ImageSwiper,
  Meta,
  ShareListingPopup,
  BreadcrumbNavigation,
  ListingNotAvailable,
} from '../../../../../components';
import {
  cityToAbbreviatedProvinceMap,
  getInitialDownPayment,
  generateStructuredData,
  getCustomerPortalBaseUrl,
  redirectToCustomerPortal,
  useIsMobileView,
} from '../../../../../utils';
import {
  GetListingDocument,
  GetSimilarListingsDocument,
  Listing,
  SearchListingsDocument,
} from '../../../../../graphql/generated';
import { client } from '../../../../../graphql/apollo-client';
import {
  FIVE_PERCENT_DECIMAL,
  fivePercentSavingsTarget,
  TWO_PERCENT_DECIMAL,
  waitListLink,
} from '../../../../../const';
import { ScrollableListingsList } from '../../../../../components/scrollable-listings-list';
import * as React from 'react';
import { useFavoriteButtonClick } from '../../../../../hooks';
import { sanitizeCityName } from '@requity-homes/utils';
import Image from 'next/image';

const repliersCdnUrl = process.env.NEXT_PUBLIC_REPLIER_CDN_URL;

const stage = process.env.NEXT_PUBLIC_STAGE;

const idToken = getCookie(cookiesIdToken);

const isValidDecimal = (str: string) => {
  const decimalRegex = /^-?\d*\.?\d+$/;
  return decimalRegex.test(str);
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const id = context.params.id.toString().toLowerCase();
  // get ip from cloudfront-viewer-address header
  const clientIp = (
    context.req.headers['cloudfront-viewer-address'] as string
  )?.split(':')[0];
  console.log('cloudfront-viewer-address', clientIp);

  const [{ data: listingData }, { data: similarListings }] = await Promise.all([
    client.query({
      query: GetListingDocument,
      variables: {
        mlsNumber: id,
        clientIp,
      },
    }),
    client.query({
      query: GetSimilarListingsDocument,
      variables: {
        mlsNumber: id,
        clientIp,
      },
    }),
  ]);

  const { data: recentListings } = await client.query({
    query: SearchListingsDocument,
    variables: {
      filterValues: {
        city: listingData?.getListing?.address?.city,
        fastSearch: true,
        minListDate: format(subDays(new Date(), 5), 'yyyy-MM-dd'),
      },
      clientIp,
    },
  });

  if (!listingData?.getListing?.mlsNumber) {
    return {
      props: {
        data: null,
      },
    };
  }

  const isServedCity = servedCities.includes(
    sanitizeCityName(listingData?.getListing?.address?.city),
  );

  if (
    context.params.city !==
      sanitizeCityName(listingData?.getListing?.address?.city) ||
    context.params.province !==
      RepliersStateToAbbreviatedProvinceMap[
        listingData?.getListing?.address?.state
      ]
  ) {
    return {
      redirect: {
        destination: `/${
          RepliersStateToAbbreviatedProvinceMap[
            listingData?.getListing?.address?.state
          ]
        }/${sanitizeCityName(
          listingData?.getListing?.address?.city,
        )}/rent-to-own/${id}/${context.params.streetName}`,
        permanent: true,
      },
    };
  }

  return {
    props: {
      data: listingData,
      statusCode: 200,
      similarListings: similarListings?.similarListings,
      recentListings:
        recentListings?.searchListings?.listings?.filter(
          (listing) => listing.mlsNumber.toLowerCase() !== id,
        ) || null,
      isServedCity,
    },
  };
}

const renderOtherHomes = (similarListings, recentListings, currentListing) => {
  const abbreviatedProvince =
    RepliersStateToAbbreviatedProvinceMap[
      currentListing?.address.state
    ]?.toUpperCase();

  return (
    <>
      {similarListings?.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">
            Similar Homes Near {currentListing?.address.city},{' '}
            {abbreviatedProvince}
          </h2>
          <ScrollableListingsList
            listings={similarListings}
            repliersCdnUrl={repliersCdnUrl}
          />
        </div>
      )}
      {recentListings?.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">
            Recently Listed Homes in {currentListing?.address.city},{' '}
            {abbreviatedProvince}
          </h2>
          <ScrollableListingsList
            listings={recentListings}
            repliersCdnUrl={repliersCdnUrl}
          />
        </div>
      )}
    </>
  );
};

export default function ListingPage({
  data,
  statusCode,
  similarListings,
  recentListings,
  isServedCity,
}) {
  const targetElementRef = useRef(null);
  const scrollToElementRef = useRef(null);
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showFavoriteTooltipText, setShowFavoriteTooltipText] = useState(false);
  const [showShareTooltipText, setShowShareTooltipText] = useState(false);
  const [selectedListingToShare, setSelectedListingToShare] =
    useState<Listing | null>(null);

  const isMobileView = useIsMobileView();

  const {
    isLoading,
    favoriteListingIds,
    setFavoriteListingIds,
    toggleListings,
  } = useFavoriteButtonClick();

  const scrollToMiddle = () => {
    scrollToElementRef.current.scrollIntoView({ behavior: 'smooth' });

    setTimeout(() => {
      setIsBannerVisible(false);
    }, 100);
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY) {
        // Scrolling down
        if (targetElementRef.current) {
          const targetElementTop =
            targetElementRef.current.getBoundingClientRect().top;
          if (targetElementTop <= 0) {
            setIsBannerVisible(false);
          }
        }
      } else {
        // Scrolling up
        if (targetElementRef.current) {
          const targetElementTop =
            targetElementRef.current.getBoundingClientRect().top;
          if (targetElementTop > window.innerHeight) {
            setIsBannerVisible(true);
          }
        }
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  const listing = data?.getListing;

  useEffect(() => {
    const listingIds = getCookie(cookiesFavoriteListings);
    if (listingIds) {
      setFavoriteListingIds(JSON.parse(listingIds));
    }
  }, [listing]);

  if (!listing) {
    return <ListingNotAvailable />;
  }

  const imageUrls = data?.getListing?.images?.map(
    (url: string) => `${repliersCdnUrl}/${url}?className=medium`,
  );

  const numBedrooms = listing?.details.numBedrooms;
  const numBathrooms = listing?.details.numBathrooms;

  const daysSpentOnRealtorDotCo = differenceInDays(
    new Date(),
    new Date(listing?.timestamps.listingUpdated),
  );

  let listingSqft = listing?.details.sqft;
  if (listing?.details.sqft?.split(' ')[1] === 'sqft') {
    listingSqft = listing.details.sqft.split(' ')[0];
  }
  const sqft = isValidDecimal(listingSqft)
    ? `${Math.round(convertStringToFloat(listing?.details.sqft))} sqft`
    : listingSqft;

  const listingFirstImage = `${repliersCdnUrl}/${listing?.images[0]}?className=medium`;

  const handleOpenNewTab = () => {
    trackEvent('listing-get-pre-qualified-click', {
      city: listing?.address.city,
    });

    window.open(
      `${getCustomerPortalBaseUrl(process.env.NEXT_PUBLIC_STAGE)}/auth/sign-up`,
      '_blank',
    );
  };

  const { totalMonthlyPayment: minimumMonthlyPayment } =
    calculateTotalMonthlyPayment(listing?.listPrice, TWO_PERCENT_DECIMAL, 5);

  const url = generateListingUrl(listing, stage);
  const structuredData = generateStructuredData([listing]);
  const cityName = capitalizeAllFirstLetters(
    listing?.address.city.toLowerCase(),
  );

  const streetName = capitalizeAllFirstLetters(
    listing?.address.streetName.toLowerCase(),
  );
  let province = cityToAbbreviatedProvinceMap[cityName];
  // if province undefined, use the province from the listing but use short form of the province
  if (!province) {
    province =
      RepliersStateToAbbreviatedProvinceMap[
        listing?.address.state
      ]?.toUpperCase();
  }
  const pageTitle =
    [streetName, listing?.address.city, province, listing?.address.zip]
      .filter(Boolean)
      .join(', ') + ' Rent-to-Own Home for Sale | Requity Homes';

  const handleFavoriteBtnClick = async (list) => {
    if (!idToken) {
      return redirectToCustomerPortal();
    }

    await toggleListings(list);
  };

  const handleCTAClick = () => {
    if (isServedCity) {
      handleOpenNewTab();
    } else {
      window.open(
        `${waitListLink}?target_city=${listing?.address.city}&target_province=${listing?.address.state}`,
        '_blank',
      );
    }
  };

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.requityhomes.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Browse Homes',
        item: 'https://www.requityhomes.com/listings',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${listing?.address.city}, ${province}`,
        item: `${url.split('rent-to-own')[0]}rent-to-own`,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: streetName,
        item: url,
      },
    ],
  };

  const description = `${numBedrooms} bedroom, ${numBathrooms} bathroom home for sale at ${streetName}, ${
    listing?.address.city
  }, ${cityToAbbreviatedProvinceMap[cityName]} ${
    listing?.address.zip
  } for ${currency(listing?.listPrice, {
    precision: 0,
  }).format()}. MLS #: ${listing?.mlsNumber.toUpperCase()}. Rent-to-own for ${currency(
    minimumMonthlyPayment,
    {
      precision: 0,
    },
  ).format()} per month with a 2% down payment.`;

  const { totalMonthlyPayment } = calculateTotalMonthlyPayment(
    listing.listPrice,
    TWO_PERCENT_DECIMAL,
    fivePercentSavingsTarget,
  );

  return (
    <div>
      <Meta
        title={pageTitle}
        description={description}
        ogImage={listingFirstImage}
        keywords={`Rent to Own, Homes, ${listing?.address.city}`}
        ogUrl={url}
        structuredData={structuredData}
        breadcrumbList={breadcrumbList}
      >
        <link
          rel="stylesheet"
          href="https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css"
        />
      </Meta>
      <>
        <div className="fixed top-0 w-full z-20">
          <Header city={listing?.address.city} />
          {isLoading && <Spinner />}
          {selectedListingToShare && (
            <ShareListingPopup
              listing={selectedListingToShare}
              handleModalClose={() => setSelectedListingToShare(null)}
            />
          )}
          <div
            className={`bg-coral-med mb-1 px-4 py-2.5 flex justify-between text-lg text-white ${
              isBannerVisible && isServedCity ? 'flex md:hidden' : 'hidden'
            }`}
          >
            <span>Starting at:</span>
            <div className="flex gap-2">
              <span>
                <span className="font-bold">
                  {currency(minimumMonthlyPayment, { precision: 0 }).format()}
                </span>
                /month
              </span>
              <div
                className="h-6 w-6 flex items-center justify-center rounded-full bg-white text-black text-md"
                onClick={scrollToMiddle}
              >
                <Icon glyph="chevronDown" />
              </div>
            </div>
          </div>
        </div>
        <div className="my-32 md:my-20"></div>
        <div className="max-w-7xl mx-auto p-6 bg-white -mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 md:gap-6">
            <div className="col-span-2 space-y-1 md:space-y-6">
              <div className="w-full h-420 md:h-620">
                {!isServedCity && (
                  <div className="md:hidden mt-1 mb-2 flex flex-col shadow-sm bg-coral-light p-3">
                    <div className="p-2">
                      <p className="text-lg font-semibold">
                        We&apos;re not currently serving this area
                      </p>
                      <p className="mt-2 text-sm">
                        Join our waitlist to be notified when we expand to this
                        location.
                      </p>
                    </div>
                    <Button
                      className="w-full text-white shadow-md-dark active:bg-coral-medium bg-coral-medium outline-none border-transparent"
                      onClick={handleCTAClick}
                    >
                      Join our waitlist
                    </Button>
                  </div>
                )}
                <BreadcrumbNavigation
                  province={listing.address.state}
                  streetName={streetName}
                />
                <ImageSwiper
                  images={imageUrls}
                  alt={`${streetName} ${listing?.address.city}, ${listing?.address.state}`}
                />
                {isMobileView && (
                  <div className="flex gap-3 justify-end">
                    <span
                      onClick={() => setSelectedListingToShare(listing)}
                      className="cursor-pointer"
                    >
                      <Icon className="h-5 w-6" glyph="share" />
                    </span>
                    <span
                      onClick={() => handleFavoriteBtnClick(listing)}
                      className="cursor-pointer"
                    >
                      {favoriteListingIds?.includes(listing.mlsNumber) ? (
                        <Icon className="h-6 w-6" glyph="heart" />
                      ) : (
                        <Icon className="h-6 w-6" glyph="outlinedHeart" />
                      )}
                    </span>
                  </div>
                )}
              </div>
              <div>
                <h1 className="text-3xl">
                  {streetName && (
                    <>
                      <span className="font-bold">{`${streetName}`}</span>{' '}
                      <br />
                    </>
                  )}
                  {listing?.address.city ? (
                    <span className="text-2xl">{`${listing?.address.city}, ${listing?.address.state} ${listing?.address.zip}`}</span>
                  ) : (
                    <span className="text-2xl">Address not available</span>
                  )}
                </h1>
                <p className="text-lg text-gray-600">{`${numBedrooms} ${
                  numBedrooms == 1 ? 'bed' : 'beds'
                } · ${numBathrooms} ${
                  numBathrooms == 1 ? 'bath' : 'baths'
                } · ${sqft}`}</p>
                <p className="mt-4">{listing?.details.description}</p>
                <div className="p-4 bg-coral-light rounded-2xl px-6 my-4 shadow-md-dark">
                  <h3 className="font-bold text-gray-800 text-md md:text-[24px]">
                    Rent-to-Own This Home for{' '}
                    {currency(totalMonthlyPayment, { precision: 0 }).format()}{' '}
                    /Month
                  </h3>

                  <p className="text-sm text-gray-700 mt-1">
                    This home is listed on realtor.ca. Requity Homes
                    doesn&lsquo;t own it—yet. But if it fits your budget and
                    meets our criteria, we can buy it for you through our
                    rent-to-own program.
                  </p>

                  <p className="text-sm text-gray-700 mt-1">
                    You move in with as little as 2% down. Part of your monthly
                    rent builds your future down payment. After your lease
                    (usually 1–3 years), you get the option to buy.
                  </p>

                  <div className="mx-auto mb-[10px]" onClick={handleCTAClick}>
                    <button className="inline-block mx-auto px-8 py-2 rounded-full font-sans font-bold text-base border cursor-pointer text-center hover:shadow-md-dark disabled:cursor-not-allowed disabled:hover:shadow-none sm:border-2 sm:px-14 sm:py-3 disabled:bg-navy-light disabled:border-navy-light mt-4 w-full text-white shadow-md-dark active:bg-coral-medium bg-coral-medium outline-none border-transparent mb-[10px]">
                      Get pre-qualified
                    </button>
                  </div>
                  <div className="flex flex-col md:flex-row">
                    <a
                      href="https://www.requityhomes.com/how-it-works"
                      rel="noreferrer noopener nofollow"
                      target="_blank"
                      className="text-sm text-orange-500 hover:text-orange-600 font-extrabold mt-2"
                    >
                      Learn more about Rent-to-Own
                      <svg
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="1em"
                        height="1em"
                        className="inline-block w-3 text-gray-600"
                      >
                        <path
                          d="M16.581 11.007 9.224 3.398a1.298 1.298 0 0 0-.423-.294 1.26 1.26 0 0 0-1.423.294 1.372 1.372 0 0 0-.378.95c0 .356.136.697.378.95l6.435 6.733-6.435 6.666a1.372 1.372 0 0 0-.378.95c0 .355.136.697.378.949.12.127.264.229.422.298.159.07.329.105.501.106.172 0 .342-.037.5-.106.159-.07.303-.17.423-.298l7.357-7.609c.133-.126.238-.28.31-.45a1.389 1.389 0 0 0-.31-1.53Z"
                          fill="currentColor"
                        />
                      </svg>
                    </a>
                    <a
                      href="https://www.requityhomes.com/post/what-homes-can-i-buy-with-requity"
                      rel="noreferrer noopener nofollow"
                      target="_blank"
                      className="text-sm text-orange-500 hover:text-orange-600 font-extrabold mt-2 md:pl-5"
                    >
                      See Qualified Home Criteria
                      <svg
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="1em"
                        height="1em"
                        className="inline-block w-3 text-gray-600"
                      >
                        <path
                          d="M16.581 11.007 9.224 3.398a1.298 1.298 0 0 0-.423-.294 1.26 1.26 0 0 0-1.423.294 1.372 1.372 0 0 0-.378.95c0 .356.136.697.378.95l6.435 6.733-6.435 6.666a1.372 1.372 0 0 0-.378.95c0 .355.136.697.378.949.12.127.264.229.422.298.159.07.329.105.501.106.172 0 .342-.037.5-.106.159-.07.303-.17.423-.298l7.357-7.609c.133-.126.238-.28.31-.45a1.389 1.389 0 0 0-.31-1.53Z"
                          fill="currentColor"
                        />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
              <div>
                <div className="border-t border-b border-gray-400 pt-4 pb-8">
                  <h2 className="text-xl font-semibold mb-4">
                    Facts & Features
                  </h2>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-2">
                        <h3 className="font-bold">Building Type</h3>{' '}
                        <p>{listing?.details.style}</p>
                      </div>
                      <div className="grid grid-cols-2">
                        <h3 className="font-bold">Year built</h3>{' '}
                        <p>{listing?.details.yearBuilt}</p>
                      </div>
                      <div className="grid grid-cols-2">
                        <h3 className="font-bold">Square Footage</h3>{' '}
                        <p>{sqft}</p>
                      </div>
                      <div className="grid grid-cols-2">
                        <h3 className="font-bold">Stories</h3>{' '}
                        <p>{listing?.condominium?.stories}</p>
                      </div>
                      <div className="grid grid-cols-2">
                        <h3 className="font-bold">Bedrooms</h3>{' '}
                        <p>{numBedrooms}</p>
                      </div>
                      <div className="grid grid-cols-2">
                        <h3 className="font-bold">Bathrooms</h3>{' '}
                        <p>{numBathrooms}</p>
                      </div>
                      <div className="grid grid-cols-2">
                        <h3 className="font-bold">Parking</h3>{' '}
                        <p>{listing?.details.numParkingSpaces}</p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-2">
                        <h3 className="font-bold">Neighbourhood</h3>
                        <p>{listing?.address.neighborhood}</p>
                      </div>
                      <div className="grid grid-cols-2">
                        <h3 className="font-bold">Land size </h3>
                        <p>{listing?.lot.acres}</p>
                      </div>
                      <div className="grid grid-cols-2">
                        <h3 className="font-bold">Heating type</h3>{' '}
                        <p>{listing?.details.heating}</p>
                      </div>
                      <div className="grid grid-cols-2">
                        <h3 className="font-bold">Basement type</h3>
                        <p>
                          {listing?.details.basement1 ||
                            listing?.details.basement2}
                        </p>
                      </div>
                      <div className="grid grid-cols-2">
                        <h3 className="font-bold">Parking Type</h3>{' '}
                        <p>
                          {listing?.details.parkingType ||
                            listing?.details.garage}
                        </p>
                      </div>
                      <div className="grid grid-cols-2">
                        <h3 className="font-bold">Time on REALTOR.ca</h3>
                        <p>{`${daysSpentOnRealtorDotCo} ${
                          daysSpentOnRealtorDotCo === 1 ? 'day' : 'days'
                        }`}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-xs md:text-right mb-2 md:mb-8">
                  <p className="mt-4">
                    Brokerage Name: {listing.office?.brokerageName}
                  </p>
                  <p>
                    <a
                      href={'https://' + listing.details.moreInformationLink}
                      rel="noreferrer noopener nofollow"
                      target="_blank"
                      className="flex justify-end"
                    >
                      <Image
                        alt="Powered by: REALTOR.ca"
                        width="90"
                        height="50"
                        src="https://www.realtor.ca/images/en-ca/powered_by_realtor.svg"
                      />
                    </a>
                  </p>
                </p>
              </div>
              <div className="hidden md:block">
                {renderOtherHomes(similarListings, recentListings, listing)}
              </div>
            </div>
            <div className="md:hidden">
              <div
                ref={scrollToElementRef}
                className="relative bottom-36 invisible"
              >
                &nbsp;
              </div>
              <div
                ref={targetElementRef}
                className="relative bottom-36 invisible"
              >
                &nbsp;
              </div>
            </div>
            <div className="space-y-6 flex flex-col shadow-sm bg-coral-light pb-24">
              <div className="rounded-t-lg p-6">
                <div className="flex justify-between">
                  <p className="text-md text-gray-600">Home price</p>
                  <div className="hidden md:flex gap-3">
                    <span
                      onClick={() => setSelectedListingToShare(listing)}
                      onMouseEnter={() => setShowShareTooltipText(true)}
                      onMouseLeave={() => setShowShareTooltipText(false)}
                      className="cursor-pointer relative"
                    >
                      {showShareTooltipText && (
                        <span className="absolute -top-6 mt-0.5 -right-2 text-coral-dark bg-white px-1 py-0.5 rounded-sm">
                          <Text useCase="legal">Share</Text>
                        </span>
                      )}
                      <Icon className="h-5 w-6" glyph="share" />
                    </span>
                    <span
                      onClick={() => handleFavoriteBtnClick(listing)}
                      className="cursor-pointer relative"
                      onMouseEnter={() => setShowFavoriteTooltipText(true)}
                      onMouseLeave={() => setShowFavoriteTooltipText(false)}
                    >
                      {showFavoriteTooltipText && (
                        <span className="absolute -top-6 mt-0.5 -right-4 text-coral-dark bg-white px-1 py-0.5 rounded-sm">
                          <Text useCase="legal">Favorite</Text>
                        </span>
                      )}
                      {favoriteListingIds?.includes(listing.mlsNumber) ? (
                        <Icon className="h-6 w-6" glyph="heart" />
                      ) : (
                        <Icon className="h-6 w-6" glyph="outlinedHeart" />
                      )}
                    </span>
                  </div>
                </div>
                <p className="text-4xl font-bold">
                  {currency(listing?.listPrice, { precision: 0 }).format()}
                </p>
                <Button
                  className="mt-4 w-full text-white shadow-md-dark active:bg-coral-medium bg-coral-medium outline-none border-transparent"
                  onClick={handleCTAClick}
                >
                  {isServedCity ? 'Get pre-qualified' : 'Join our waitlist'}
                </Button>
                <div className="pt-2 text-center">
                  <a
                    href="https://www.requityhomes.com/rent-to-own-affordability-calculator"
                    className="text-coral-dark underline text-sm"
                    rel="noreferrer noopener nofollow"
                    target="_blank"
                  >
                    Calculate my rent-to-own affordability
                  </a>{' '}
                  <svg
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="1em"
                    height="1em"
                    className="inline-block w-3"
                  >
                    <path
                      d="M16.581 11.007 9.224 3.398a1.298 1.298 0 0 0-.423-.294 1.26 1.26 0 0 0-1.423.294 1.372 1.372 0 0 0-.378.95c0 .356.136.697.378.95l6.435 6.733-6.435 6.666a1.372 1.372 0 0 0-.378.95c0 .355.136.697.378.949.12.127.264.229.422.298.159.07.329.105.501.106.172 0 .342-.037.5-.106.159-.07.303-.17.423-.298l7.357-7.609c.133-.126.238-.28.31-.45a1.389 1.389 0 0 0-.31-1.53Z"
                      fill="currentColor"
                    ></path>
                  </svg>
                </div>
              </div>
              {isServedCity && listing?.listPrice && (
                <div className="space-y-4 flex flex-col gap-4 flex-1 p-6 pt-0">
                  <PaymentOption
                    title="Start with 2% down and save toward 5% in 3 years*"
                    listPrice={listing.listPrice}
                    initialDownPaymentPercentInDecimal={TWO_PERCENT_DECIMAL}
                    savingsTargetPercent={5}
                    footnote="* Exact down payment ranges from 2-10% based on your risk profile and will be assessed during the full approval process."
                  />
                  <PaymentOption
                    title="Start with 5% down and save toward 5% in 3 years."
                    listPrice={listing.listPrice}
                    initialDownPaymentPercentInDecimal={FIVE_PERCENT_DECIMAL}
                    savingsTargetPercent={5}
                  />
                </div>
              )}
              {!isServedCity && (
                <div className="p-6">
                  <p className="text-lg font-semibold">
                    We&apos;re not currently serving this area
                  </p>
                  <p className="mt-2">
                    Join our waitlist to be notified when we expand to this
                    location.
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="md:hidden w-full mt-8">
            {renderOtherHomes(similarListings, recentListings, listing)}
          </div>
          <div className="multilingual-cta bg-[#098080] text-white py-[50px] px-5 my-10 rounded-[10px] text-center">
            <div className="container max-w-[800px] mx-auto">
              <h2 className="text-[28px] font-bold mb-[15px]">
                Book a Call with Requity Homes
              </h2>
              <p className="text-[16px] mb-[25px]">
                We speak English, Mandarin, Tagalog, Russian, Twi, Fante, and
                Hindi. Our team is ready to help you navigate the rent-to-own
                process.
              </p>
              <div className="flex flex-wrap justify-center gap-[20px] mb-[20px]">
                <a
                  rel="noreferrer noopener nofollow"
                  target="_blank"
                  href="https://meetings.hubspot.com/amyding/intro-call-w-requity"
                  className="bg-[#ffa38d] text-white font-bold py-[15px] px-[30px] rounded-[30px] border-none cursor-pointer text-[16px] transition-all duration-300 ease-in-out"
                >
                  Book a Consultation Call
                </a>
                <a
                  rel="noreferrer noopener nofollow"
                  target="_blank"
                  href="https://portal.requityhomes.com/auth/sign-up"
                  className="bg-transparent text-white font-bold py-[15px] px-[30px] rounded-[30px] border-2 border-white cursor-pointer text-[16px] transition-all duration-300 ease-in-out"
                >
                  Rent-to-Own This Home
                </a>
              </div>
              <p className="text-[14px] opacity-80">
                Just provide your information and preferred language.
                We&lsquo;ll take care of the rest!
              </p>
            </div>
          </div>
        </div>

        <Footer city={listing?.address.city} />
      </>
    </div>
  );
}

function PaymentOption(props) {
  const [showDetails, setShowDetails] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState('');

  const { rent, savings, totalMonthlyPayment } = calculateTotalMonthlyPayment(
    props.listPrice,
    props.initialDownPaymentPercentInDecimal,
    props.savingsTargetPercent,
  );

  const tooltipType = {
    INITIAL_PAYMENT: 'INITIAL_PAYMENT',
    TARGET_SAVINGS: 'TARGET_SAVINGS',
    BEFORE_TWELVE_MONTHS: 'BEFORE_TWELVE_MONTHS',
    BEFORE_TWENTYFOUR_MONTHS: 'BEFORE_TWENTYFOUR_MONTHS',
    BEFORE_THIRTYSIX_MONTHS: 'BEFORE_THIRTYSIX_MONTHS',
  };

  const initialDownPayment = getInitialDownPayment(
    props.listPrice,
    props.initialDownPaymentPercentInDecimal,
  );

  let initialDownPaymentPercent = (initialDownPayment / props.listPrice) * 100;

  initialDownPaymentPercent = Number.isInteger(initialDownPaymentPercent)
    ? initialDownPaymentPercent
    : Number(initialDownPaymentPercent.toFixed(1));

  const yearThreeBuyBackPrice = props.listPrice * 1.05 ** 3;
  const yearTwoBuyBackPrice = props.listPrice * 1.05 ** 2;
  const yearOneBuyBackPrice = props.listPrice * 1.05;

  return (
    <div className="p-6 bg-white rounded-3xl shadow-md-dark">
      <div className="flex justify-between">
        <p className="text-sm text-gray-600 mb-2">{props.title}</p>
        <span onClick={() => setShowDetails(!showDetails)}>
          <Icon glyph={showDetails ? 'chevronUp' : 'chevronDown'} />
        </span>
      </div>
      {props.footnote && (
        <p className="font-thin text-xs text-gray-600 mb-2">{props.footnote}</p>
      )}
      <p className="text-2xl font-bold pb-1">
        {currency(totalMonthlyPayment, { precision: 0 }).format()}{' '}
        <span className="text-sm">/ month</span>
      </p>
      {showDetails ? (
        <>
          <div className="mt-4">
            <p className="text-md font-bold">Rent-to-own Plan</p>
            <p className="flex justify-between relative">
              {activeTooltip === tooltipType.INITIAL_PAYMENT && (
                <Tooltip>
                  Your initial payment that goes toward your down payment
                  savings
                </Tooltip>
              )}
              <span
                className="border-b border-dashed border-gray-600 cursor-pointer"
                onMouseEnter={() =>
                  setActiveTooltip(tooltipType.INITIAL_PAYMENT)
                }
                onMouseLeave={() => setActiveTooltip('')}
              >
                Initial deposit
              </span>{' '}
              <span>
                {currency(initialDownPayment, { precision: 0 }).format()}{' '}
                {`(${initialDownPaymentPercent}%)`}
              </span>
            </p>
            <p className="flex justify-between relative">
              {activeTooltip === tooltipType.TARGET_SAVINGS && (
                <Tooltip>
                  Your down payment savings target toward the end of the program
                </Tooltip>
              )}
              <span
                className="border-b border-dashed border-gray-600 cursor-pointer"
                onMouseEnter={() =>
                  setActiveTooltip(tooltipType.TARGET_SAVINGS)
                }
                onMouseLeave={() => setActiveTooltip('')}
              >
                Savings target
              </span>{' '}
              <span>
                {currency(
                  (yearThreeBuyBackPrice / 100) * props.savingsTargetPercent,
                  { precision: 0 },
                ).format()}{' '}
                {`(${props.savingsTargetPercent}%)`}
              </span>
            </p>
          </div>
          <div className="mt-4">
            <p className="text-md font-bold">Monthly payment</p>
            <p className="flex justify-between">
              <span>Rent</span>{' '}
              <span>{currency(rent, { precision: 0 }).format()}</span>
            </p>
            <p className="flex justify-between">
              <span>Savings</span>{' '}
              <span>{currency(savings, { precision: 0 }).format()}</span>
            </p>
          </div>
          <div className="mt-4">
            <p className="text-md font-bold">Buyback price</p>
            <p className="flex justify-between relative">
              {activeTooltip === tooltipType.BEFORE_TWELVE_MONTHS && (
                <Tooltip>
                  Available to those who buy their home from Requity Homes
                  before the end of the 12th month of their lease.
                </Tooltip>
              )}
              <span
                className="border-b border-dashed border-gray-600 cursor-pointer"
                onMouseEnter={() =>
                  setActiveTooltip(tooltipType.BEFORE_TWELVE_MONTHS)
                }
                onMouseLeave={() => setActiveTooltip('')}
              >
                Before 12 months
              </span>{' '}
              <span>
                {currency(yearOneBuyBackPrice, { precision: 0 }).format()}
              </span>
            </p>
            <p className="flex justify-between relative">
              {activeTooltip === tooltipType.BEFORE_TWENTYFOUR_MONTHS && (
                <Tooltip>
                  Available to those who buy their home from Requity Homes
                  before the end of the 24th month of their lease.
                </Tooltip>
              )}
              <span
                className="border-b border-dashed border-gray-600 cursor-pointer"
                onMouseEnter={() =>
                  setActiveTooltip(tooltipType.BEFORE_TWENTYFOUR_MONTHS)
                }
                onMouseLeave={() => setActiveTooltip('')}
              >
                Before 24 months
              </span>{' '}
              <span>
                {currency(yearTwoBuyBackPrice, {
                  precision: 0,
                }).format()}
              </span>
            </p>
            <p className="flex justify-between relative">
              {activeTooltip === tooltipType.BEFORE_THIRTYSIX_MONTHS && (
                <Tooltip>
                  Available to those who buy their home from Requity Homes
                  before the end of the 36th month of their lease.
                </Tooltip>
              )}
              <span
                className="border-b border-dashed border-gray-600 cursor-pointer"
                onMouseEnter={() =>
                  setActiveTooltip(tooltipType.BEFORE_THIRTYSIX_MONTHS)
                }
                onMouseLeave={() => setActiveTooltip('')}
              >
                Before 36 months
              </span>{' '}
              <span>
                {currency(yearThreeBuyBackPrice, {
                  precision: 0,
                }).format()}
              </span>
            </p>
          </div>
        </>
      ) : (
        <div className="">
          <div className="mb-4 border-b pb-1">
            <p className="flex justify-between">
              <span>Rent</span>{' '}
              <span className="font-bold">
                {currency(rent, { precision: 0 }).format()}
              </span>
            </p>
            <p className="flex justify-between">
              <span>Savings</span>{' '}
              <span className="font-bold">
                {currency(savings, { precision: 0 }).format()}
              </span>
            </p>
          </div>
          <div>
            <p className="flex justify-between">
              <span>Initial deposit</span>{' '}
              <span className="font-bold">
                {props.initialDownPaymentPercentInDecimal * 100}%
              </span>
            </p>
            <p className="flex justify-between">
              <span>Savings target</span>{' '}
              <span className="font-bold">
                Fixed at {props.savingsTargetPercent}%
              </span>
            </p>
          </div>
          <div className="pt-2">
            <a
              href="https://www.requityhomes.com/rent-to-own-payment-calculator"
              rel="noreferrer noopener nofollow"
              target="_blank"
              className="text-coral-dark underline text-sm"
            >
              Calculate my rent-to-own payments
            </a>{' '}
            <svg
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="1em"
              height="1em"
              className="inline-block w-3"
            >
              <path
                d="M16.581 11.007 9.224 3.398a1.298 1.298 0 0 0-.423-.294 1.26 1.26 0 0 0-1.423.294 1.372 1.372 0 0 0-.378.95c0 .356.136.697.378.95l6.435 6.733-6.435 6.666a1.372 1.372 0 0 0-.378.95c0 .355.136.697.378.949.12.127.264.229.422.298.159.07.329.105.501.106.172 0 .342-.037.5-.106.159-.07.303-.17.423-.298l7.357-7.609c.133-.126.238-.28.31-.45a1.389 1.389 0 0 0-.31-1.53Z"
                fill="currentColor"
              ></path>
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}

const Tooltip = ({ children }) => (
  <p className="absolute w-60 text-sm left-10 md:-left-64 p-1 pl-2 bg-black text-white rounded z-10">
    {children}
  </p>
);
